import {
  CSRF_HEADER,
  type DeviceAuthorization,
  deviceAuthorizationSchema,
  deviceDecisionSchema,
  ESI_CLI_CLIENT_ID,
} from "@esi/shared";
import { type FormEvent, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import { formatCountry, formatDateTime } from "../i18n/format.ts";
import { PermissionList, writeConfirmed } from "./Permissions.tsx";
import { StandaloneState } from "./SignIn.tsx";
import { signInNavigation } from "./session-ended.ts";

/** ESI's sign-in page, coming back to the device page with the code typed so far. */
export const deviceSignInPath = (code: string) =>
  `/signin?returnTo=${encodeURIComponent(`/device${code ? `?user_code=${encodeURIComponent(code)}` : ""}`)}`;

/** A user code as typed, shown as the client shows it: upper case, `XXXX-XXXX`. */
export function formatUserCode(input: string): string {
  const letters = input
    .toUpperCase()
    .replace(/[^A-Z]/g, "")
    .slice(0, 8);
  return letters.length > 4 ? `${letters.slice(0, 4)}-${letters.slice(4)}` : letters;
}

type Step =
  | { kind: "code" }
  | { kind: "confirm"; device: DeviceAuthorization }
  | { kind: "done"; outcome: "approved" | "denied"; client: string };

/**
 * ESI's device verification page (openspec change native-device-and-cimd, design D1): a
 * headless client shows a code; the signed-in user types it here, then sees who is asking
 * (application and its domain, access, where and when the request started, organisation and
 * account) and approves or denies explicitly. Entering the code alone approves nothing, and a
 * code from the address bar is only prefilled, never submitted, so the user compares it with
 * the one on the client's screen.
 */
export function DevicePage({
  initialCode,
  csrfToken,
}: {
  initialCode: string | null;
  csrfToken: string;
}) {
  const { t } = useTranslation();
  useDocumentTitle(t("device.documentTitle"));
  const [code, setCode] = useState(formatUserCode(initialCode ?? ""));
  const [step, setStep] = useState<Step>({ kind: "code" });
  const [busy, setBusy] = useState<"lookup" | "confirm" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();
  const errorId = useId();
  const headers = { headers: { [CSRF_HEADER]: csrfToken } };

  function refusal(e: unknown): string {
    if (e instanceof ApiError) {
      if (e.status === 401) {
        signInNavigation.go(deviceSignInPath(code));
        return t("device.errors.signIn");
      }
      if (e.status === 404) {
        return t("device.errors.invalid");
      }
      if (e.status === 429) {
        return t("device.errors.throttled");
      }
      if (e.code === "device_grant_disabled") {
        return t("device.errors.deviceGrantDisabled");
      }
      if (e.code === "client_not_allowed") {
        return t("device.errors.clientNotAllowed");
      }
      if (e.status === 403) {
        return e.explained ? e.message : t("device.errors.otherSession");
      }
      return e.message;
    }
    return t("errors.network");
  }

  async function lookup(event: FormEvent) {
    event.preventDefault();
    if (code.replace("-", "").length !== 8) {
      setError(t("device.errors.format"));
      return;
    }
    setBusy("lookup");
    setError(null);
    try {
      const device = deviceAuthorizationSchema.parse(
        await ok(await api.api.oauth.verify.lookup.$post({ json: { userCode: code } }, headers)),
      );
      setStep({ kind: "confirm", device });
    } catch (e) {
      setError(refusal(e));
    } finally {
      setBusy(null);
    }
  }

  async function decide(device: DeviceAuthorization, choice: "confirm" | "deny") {
    setBusy(choice);
    setError(null);
    try {
      const json = { json: { userCode: device.userCode } };
      // `write` only when this page named it (mcp-write-tools D2).
      const confirm = { json: { ...json.json, write: writeConfirmed(device.scopes) } };
      const { status } = deviceDecisionSchema.parse(
        choice === "confirm"
          ? await ok(await api.api.oauth.verify.confirm.$post(confirm, headers))
          : await ok(await api.api.oauth.verify.deny.$post(json, headers)),
      );
      setStep({ kind: "done", outcome: status, client: device.client.name });
    } catch (e) {
      setError(refusal(e));
    } finally {
      setBusy(null);
    }
  }

  if (step.kind === "done") {
    return (
      <StandaloneState
        title={step.outcome === "approved" ? t("device.approvedTitle") : t("device.deniedTitle")}
      >
        <Notice tone={step.outcome === "approved" ? "success" : "neutral"} live>
          {step.outcome === "approved"
            ? t("device.approved", { client: step.client })
            : t("device.denied", { client: step.client })}
        </Notice>
        <p className="text-sm leading-relaxed text-fg-secondary">{t("device.closeHint")}</p>
      </StandaloneState>
    );
  }

  if (step.kind === "confirm") {
    const { device } = step;
    const user = device.user.displayName
      ? `${device.user.displayName} (${device.user.email})`
      : device.user.email;
    const firstParty = device.client.id === ESI_CLI_CLIENT_ID;
    return (
      <StandaloneState title={t("connect.title", { client: device.client.name })}>
        <p className="text-sm leading-relaxed text-fg-secondary">{t("device.confirmLead")}</p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-fg-secondary">{t("connect.client")}</dt>
          <dd className="min-w-0">
            <span className="font-medium text-fg">{device.client.name}</span>
            <span className="block break-all text-xs text-fg-muted">
              {firstParty || !device.client.host
                ? t("connect.firstParty")
                : t("connect.hostHint", { host: device.client.host })}
            </span>
          </dd>
          <dt className="text-fg-secondary">{t("device.code")}</dt>
          <dd className="font-mono tabular-nums text-fg">{device.userCode}</dd>
          <dt className="text-fg-secondary">{t("connect.resource")}</dt>
          <dd className="min-w-0">
            <span className="text-fg">{t(`connect.resources.${device.resource}`)}</span>
            {device.resource === "api" ? (
              <span className="block text-xs text-fg-muted">{t("connect.resourceHints.api")}</span>
            ) : null}
          </dd>
          <dt className="text-fg-secondary">{t("connect.permissions.label")}</dt>
          <dd className="min-w-0">
            <PermissionList scopes={device.scopes} resource={device.resource} />
          </dd>
          <dt className="text-fg-secondary">{t("device.origin")}</dt>
          <dd className="text-fg">
            {device.origin.country
              ? formatCountry(device.origin.country)
              : t("device.unknownCountry")}
            <span className="block text-xs tabular-nums text-fg-muted">
              {t("device.startedAt", { time: formatDateTime(device.origin.startedAt) })}
            </span>
          </dd>
          <dt className="text-fg-secondary">{t("connect.tenant")}</dt>
          <dd className="text-fg">{device.tenant.name}</dd>
          <dt className="text-fg-secondary">{t("connect.user")}</dt>
          <dd className="break-words text-fg">{user}</dd>
        </dl>
        <Notice tone="warning">{t("device.warning")}</Notice>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <div className="flex gap-2">
          <Button disabled={busy !== null} onClick={() => void decide(device, "confirm")}>
            {busy === "confirm" ? t("connect.allowing") : t("device.approve")}
          </Button>
          <Button
            variant="outline"
            disabled={busy !== null}
            onClick={() => void decide(device, "deny")}
          >
            {t("connect.deny")}
          </Button>
        </div>
      </StandaloneState>
    );
  }

  return (
    <StandaloneState title={t("device.title")}>
      <p className="text-sm leading-relaxed text-fg-secondary">{t("device.lead")}</p>
      <form className="flex flex-col gap-4" onSubmit={(e) => void lookup(e)} noValidate>
        <div className="flex flex-col gap-2">
          <Label htmlFor={inputId}>{t("device.codeLabel")}</Label>
          <Input
            id={inputId}
            value={code}
            onChange={(e) => setCode(formatUserCode(e.target.value))}
            autoComplete="one-time-code"
            autoCapitalize="characters"
            spellCheck={false}
            inputMode="text"
            placeholder="BCDF-GHJK"
            maxLength={9}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className="h-10 font-mono text-base tracking-wider tabular-nums"
            autoFocus
          />
        </div>
        {error ? (
          <div id={errorId}>
            <Notice tone="danger">{error}</Notice>
          </div>
        ) : null}
        <Button type="submit" disabled={busy !== null}>
          {busy === "lookup" ? t("device.checking") : t("device.continue")}
        </Button>
      </form>
    </StandaloneState>
  );
}
